export interface GalleryPhoto {
  image?: string | null;
  image_media?: {
    width?: number | null;
    height?: number | null;
    classification?: string | null;
  } | null;
  alt_text?: string | null;
  title?: string | null;
  description?: string | null;
  source?: string | null;
  source_page_urls?: string | null;
  original_url?: string | null;
}

/**
 * Hero image intent for one inner page. Profiles are allocated in array
 * order, so pages earlier in the list get first pick of the gallery and
 * later pages take the best image that is still unused.
 */
interface HeroProfile {
  hint: string;
  paths: string[];
  /** Subjects that make a photo a strong fit for this page (weight 3). */
  primary: string[];
  /** Acceptable supporting subjects (weight 1). */
  secondary: string[];
  /** Interior pages prefer staged / furnished photos over empty units. */
  interior?: boolean;
  /** Subjects that would otherwise be penalised but suit this page. */
  allow?: string[];
}

const INTERIOR_PRIMARY = [
  'kitchen', 'living room', 'living area', 'living space', 'bedroom',
  'interior', 'dining', 'great room', 'family room', 'master suite',
];
const INTERIOR_SECONDARY = [
  'apartment', 'unit', 'home', 'bathroom', 'closet', 'hardwood', 'island',
  'countertop', 'cabinet', 'appliance', 'den', 'suite', 'inside',
];
const AMENITY_PRIMARY = [
  'pool', 'clubhouse', 'fitness', 'gym', 'lounge', 'courtyard', 'rooftop',
  'grill', 'firepit', 'fire pit', 'spa', 'sundeck', 'cabana', 'amenity',
  'amenities',
];
const AMENITY_SECONDARY = [
  'playground', 'dog park', 'park', 'yoga', 'coworking', 'business center',
  'game room', 'theater', 'patio', 'terrace', 'garden', 'recreation',
  'outdoor', 'resort', 'tennis', 'pickleball', 'basketball', 'trail',
];
const EXTERIOR_PRIMARY = [
  'exterior', 'building', 'entrance', 'entry', 'facade', 'community',
  'property', 'leasing office', 'leasing center', 'front',
];
const EXTERIOR_SECONDARY = [
  'lobby', 'welcome', 'signage', 'monument', 'landscaping', 'architecture',
  'aerial', 'drone', 'courtyard', 'dusk', 'twilight', 'grounds',
];
const NEIGHBORHOOD_PRIMARY = [
  'neighborhood', 'downtown', 'skyline', 'street', 'streetscape', 'city',
  'landmark', 'aerial', 'drone', 'local', 'nearby', 'area',
];
const NEIGHBORHOOD_SECONDARY = [
  'park', 'shopping', 'dining', 'restaurant', 'cafe', 'shop', 'district',
  'view', 'surrounding', 'location', 'lake', 'river', 'waterfront', 'trail',
];
const LIFESTYLE_PRIMARY = ['lifestyle', 'resident', 'residents', 'people', 'lounge', 'clubhouse'];

const HERO_PROFILES: HeroProfile[] = [
  {
    hint: 'floor-plans',
    paths: ['/floor-plans'],
    primary: INTERIOR_PRIMARY,
    secondary: INTERIOR_SECONDARY,
    interior: true,
  },
  {
    hint: 'amenities',
    paths: ['/amenities'],
    primary: AMENITY_PRIMARY,
    secondary: AMENITY_SECONDARY,
  },
  {
    hint: 'gallery',
    paths: ['/gallery'],
    primary: [...EXTERIOR_PRIMARY, 'aerial', 'twilight', 'dusk'],
    secondary: [...EXTERIOR_SECONDARY, ...AMENITY_PRIMARY],
  },
  {
    hint: 'neighborhood',
    paths: ['/neighborhood'],
    primary: NEIGHBORHOOD_PRIMARY,
    secondary: NEIGHBORHOOD_SECONDARY,
  },
  {
    hint: 'virtual-tour',
    paths: ['/virtual-tour', '/virtual-tours'],
    primary: INTERIOR_PRIMARY,
    secondary: [...INTERIOR_SECONDARY, 'tour', 'model', 'walkthrough'],
    interior: true,
  },
  {
    hint: 'contact',
    paths: ['/contact-us', '/contact'],
    primary: EXTERIOR_PRIMARY,
    secondary: [...EXTERIOR_SECONDARY, 'reception', 'concierge', 'office'],
  },
  {
    hint: 'schedule-a-tour',
    paths: ['/schedule-a-tour'],
    primary: ['leasing office', 'leasing center', 'lobby', 'entrance', 'clubhouse', 'reception'],
    secondary: [...EXTERIOR_PRIMARY, 'welcome', 'concierge', 'tour'],
  },
  {
    hint: 'apply-now',
    paths: ['/apply-now'],
    primary: INTERIOR_PRIMARY,
    secondary: INTERIOR_SECONDARY,
    interior: true,
  },
  {
    hint: 'interactive-map',
    paths: ['/interactive-map'],
    primary: NEIGHBORHOOD_PRIMARY,
    secondary: [...NEIGHBORHOOD_SECONDARY, 'exterior', 'building'],
  },
  {
    hint: 'pet-policy',
    paths: ['/pet-policy'],
    primary: ['dog', 'pet', 'dog park', 'pet spa', 'dog wash', 'paw', 'puppy', 'cat'],
    secondary: ['park', 'courtyard', 'lawn', 'green space', 'trail', 'outdoor', 'grass'],
  },
  {
    hint: 'reviews',
    paths: ['/reviews'],
    primary: [...LIFESTYLE_PRIMARY, 'community'],
    secondary: [...EXTERIOR_PRIMARY, ...AMENITY_PRIMARY],
  },
  {
    hint: 'resident-access',
    paths: ['/resident-access'],
    primary: ['entrance', 'entry', 'lobby', 'mailroom', 'package', 'resident', 'residents'],
    secondary: EXTERIOR_PRIMARY,
  },
  {
    hint: 'faq',
    paths: ['/frequently-asked-questions', '/faq'],
    primary: ['leasing office', 'leasing center', 'lobby', 'clubhouse', 'reception', 'concierge'],
    secondary: [...EXTERIOR_PRIMARY, ...AMENITY_PRIMARY],
  },
  {
    hint: 'flexible-living-programs',
    paths: ['/flexible-living-programs'],
    primary: [...LIFESTYLE_PRIMARY, 'living room', 'living area', 'living space'],
    secondary: [...INTERIOR_SECONDARY, ...AMENITY_PRIMARY],
  },
  {
    hint: 'rewards-programs',
    paths: ['/rewards-programs'],
    primary: [...LIFESTYLE_PRIMARY, 'community'],
    secondary: AMENITY_PRIMARY,
  },
  {
    hint: 'better-built-living',
    paths: ['/better-built-living'],
    primary: ['construction', 'modular', 'steel', 'architecture', 'building', 'exterior', 'facade'],
    secondary: ['quality', 'design', 'finishes', 'craftsmanship', 'property'],
    allow: ['construction'],
  },
  {
    hint: 'thank-you',
    paths: ['/thank-you'],
    primary: ['community', 'courtyard', 'exterior', 'dusk', 'twilight', 'lifestyle'],
    secondary: [...EXTERIOR_SECONDARY, ...AMENITY_PRIMARY],
  },
  {
    hint: 'privacy-policy',
    paths: ['/privacy-policy'],
    primary: EXTERIOR_PRIMARY,
    secondary: EXTERIOR_SECONDARY,
  },
  {
    hint: 'accessibility',
    paths: ['/accessibility'],
    primary: ['entrance', 'entry', 'elevator', 'lobby', 'walkway', 'ramp', 'exterior'],
    secondary: EXTERIOR_PRIMARY,
  },
  {
    hint: 'sitemap',
    paths: ['/sitemap'],
    primary: ['aerial', 'drone', 'overview', 'exterior', 'community'],
    secondary: EXTERIOR_PRIMARY,
  },
];

/** Fallback when nothing matches a page: a general property / community shot. */
const GENERIC_PROFILE: HeroProfile = {
  hint: '',
  paths: [],
  primary: [...EXTERIOR_PRIMARY, 'aerial', 'drone', 'courtyard'],
  secondary: EXTERIOR_SECONDARY,
};

/** Photos that read badly as a full-bleed banner — diagrams, graphics, poor shots. */
const AVOID_TERMS = [
  'floorplan', 'floor plan', 'site plan', 'siteplan', 'blueprint', 'diagram',
  'map', 'logo', 'icon', 'graphic', 'flyer', 'brochure', 'screenshot', 'text',
  'coming soon', 'placeholder', 'blurry', 'blurred', 'construction',
  'qr', 'headshot',
];
const STAGED_TERMS = ['staged', 'staging', 'model', 'furnished', 'decorated', 'virtually staged'];

function getUrlPath(url: string | null | undefined): string {
  if (!url) return '';

  try {
    return decodeURIComponent(new URL(url).pathname);
  } catch {
    return '';
  }
}

function photoText(photo: GalleryPhoto): string {
  const parts = [
    photo.title,
    photo.description,
    photo.alt_text,
    photo.image_media?.classification,
    getUrlPath(photo.image),
    getUrlPath(photo.original_url),
    photo.source_page_urls,
  ];

  const text = parts
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ');

  return ` ${text} `;
}

function hasTerm(text: string, term: string): boolean {
  const normalized = term.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (!normalized) return false;
  return (
    text.includes(` ${normalized} `) ||
    text.includes(` ${normalized}s `) ||
    text.includes(` ${normalized}es `)
  );
}

function countTerms(text: string, terms: string[]): number {
  return terms.reduce((count, term) => count + (hasTerm(text, term) ? 1 : 0), 0);
}

function relevanceScore(text: string, profile: HeroProfile, extraKeywords: string[]): number {
  const avoid = AVOID_TERMS.filter((term) => !profile.allow?.includes(term));
  if (countTerms(text, avoid) > 0) return 0;

  const score =
    countTerms(text, profile.primary) * 3 +
    countTerms(text, profile.secondary) +
    countTerms(text, extraKeywords);

  if (score > 0 && profile.interior && countTerms(text, STAGED_TERMS) > 0) {
    return score + 3;
  }
  return score;
}

/** Resolution and banner-friendly aspect ratio; unknown dimensions are neutral. */
function qualityScore(photo: GalleryPhoto, text: string): number {
  let score = countTerms(text, STAGED_TERMS) > 0 ? 1 : 0;
  const width = photo.image_media?.width ?? 0;
  const height = photo.image_media?.height ?? 0;

  if (width > 0) {
    if (width < 800) score -= 4;
    else if (width < 1200) score -= 1;
    else if (width >= 1600) score += 1;
  }

  if (width > 0 && height > 0) {
    const ratio = width / height;
    if (ratio < 1) score -= 3;
    else if (ratio >= 1.3) score += 1;
  }

  return score;
}

/** Low-res or portrait photos that crop badly into a wide banner. */
const POOR_QUALITY = -3;

interface ScoredPhoto {
  url: string;
  text: string;
  quality: number;
  usable: boolean;
}

function scoreGallery(gallery: GalleryPhoto[]): ScoredPhoto[] {
  return gallery.map((photo) => {
    const url = photo.image || '';
    const text = photoText(photo);
    return {
      url,
      text,
      quality: qualityScore(photo, text),
      usable: Boolean(url) && countTerms(text, AVOID_TERMS) === 0,
    };
  });
}

function profileFor(hint: string, keywords: string[]): HeroProfile {
  return (
    HERO_PROFILES.find((profile) => profile.hint === hint) ?? {
      hint,
      paths: [],
      primary: keywords,
      secondary: EXTERIOR_PRIMARY,
    }
  );
}

function pickForProfile(
  scored: ScoredPhoto[],
  profile: HeroProfile,
  keywords: string[],
  used: Map<string, number>,
): number {
  let best = -1;
  let bestKey: [number, number, number, number] | null = null;

  for (let index = 0; index < scored.length; index += 1) {
    const photo = scored[index];
    if (!photo.url) continue;

    const uses = used.get(photo.url) ?? 0;
    const poor = photo.quality <= POOR_QUALITY;
    let relevance = relevanceScore(photo.text, profile, keywords);
    // Priority: unique+relevant, reused+relevant, low-quality relevant, then a
    // general property shot (unique before reused), then any unused usable photo.
    let tier = 0;
    if (relevance > 0 && !poor) tier = uses === 0 ? 6 : 5;
    else if (relevance > 0) tier = 4;
    else if (photo.usable && !poor) {
      relevance = relevanceScore(photo.text, GENERIC_PROFILE, []);
      if (relevance > 0) tier = uses === 0 ? 3 : 2;
      else if (uses === 0) tier = 1;
    }
    if (tier === 0) continue;

    const key: [number, number, number, number] = [tier, relevance, photo.quality, -uses];
    if (!bestKey || compareKeys(key, bestKey) > 0) {
      best = index;
      bestKey = key;
    }
  }

  return best;
}

function compareKeys(a: number[], b: number[]): number {
  for (let index = 0; index < a.length; index += 1) {
    if (a[index] !== b[index]) return a[index] - b[index];
  }
  return 0;
}

function isActive(profile: HeroProfile, pagePaths: string[] | undefined): boolean {
  if (!pagePaths?.length) return true;
  return profile.paths.some((path) => pagePaths.includes(path));
}

/**
 * Deterministic site-wide allocation: every PageHero instance runs the same
 * pass over the same gallery, so each page lands on a distinct image without
 * needing to know what the other pages rendered.
 */
export function pickHeroImage(
  gallery: GalleryPhoto[],
  keywords: string[],
  pageHint: string,
  options: {
    pagePaths?: string[];
    reservedImages?: Array<string | null | undefined>;
    /** Images already locked on other pages (editor or generator overrides), keyed by page hint. */
    fixedImages?: Record<string, string | null | undefined>;
  } = {},
): number {
  if (!gallery.length) return -1;

  const scored = scoreGallery(gallery);
  const used = new Map<string, number>();
  const reserve = (url: string | null | undefined) => {
    if (url) used.set(url, (used.get(url) ?? 0) + 1);
  };
  (options.reservedImages ?? []).forEach(reserve);

  const current = profileFor(pageHint, keywords);
  const fixed = options.fixedImages ?? {};

  for (const [hint, url] of Object.entries(fixed)) {
    if (hint !== current.hint) reserve(url);
  }

  for (const profile of HERO_PROFILES) {
    if (profile.hint === current.hint) break;
    if (fixed[profile.hint] || !isActive(profile, options.pagePaths)) continue;

    const index = pickForProfile(scored, profile, [], used);
    if (index >= 0) {
      const url = scored[index].url;
      used.set(url, (used.get(url) ?? 0) + 1);
    }
  }

  // Known profiles ignore per-instance keywords so every page reproduces the
  // same allocation for the pages ahead of it.
  const isKnown = HERO_PROFILES.includes(current);
  return pickForProfile(scored, current, isKnown ? [] : keywords, used);
}
